import { useState, useCallback, useEffect } from "react";
import { Insurance } from "@/types/insurance";
import insuranceService from "@/services/insurance.service";
import { MemberInfo } from "@/types/member";

interface UseInsuranceProps {
  memberInfo: MemberInfo & { onBehalf: boolean };
}

export function useInsurance({ memberInfo }: UseInsuranceProps) {
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [familyInsurances, setFamilyInsurances] = useState<Insurance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  // Reset states when member changes
  useEffect(() => {
    setInsurances([]);
    setFamilyInsurances([]);
    setError(null);
    setFetched(false);
  }, [memberInfo?.id]);

  const fetchInsurance = useCallback(
    async (skipLifetime: boolean = false) => {
      if (!memberInfo?.id) return [];

      setLoading(true);
      setError(null);
      try {
        const data = await insuranceService.getInsuranceByMember(memberInfo.id);
        let filteredData = Array.isArray(data) ? data : [];
        if (skipLifetime) {
          filteredData = filteredData.filter(
            (insurance) => insurance.isLifetimePolicy !== true
          );
        }
        setInsurances(filteredData);
        setFetched(true);
        return filteredData;
      } catch (err: any) {
        const errorMsg = err?.message || "Error fetching insurance";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [memberInfo?.id]
  );

  const createInsurance = useCallback(
    async (payload: Omit<Insurance, "id">) => {
      if (!memberInfo?.id) {
        throw new Error("Member info not available");
      }

      setLoading(true);
      setError(null);
      try {
        // Ensure familyId is set from memberInfo if not provided
        const payloadWithFamily = {
          ...payload,
          familyId:
            payload.familyId ||
            memberInfo.current_family ||
            memberInfo.familyId,
        };

        const data = await insuranceService.createInsurance(payloadWithFamily);
        setInsurances((prev) => [...prev, data]);
        return data;
      } catch (err: any) {
        const errorMsg = err?.message || "Error creating insurance";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [memberInfo]
  );

  const updateInsurance = useCallback(
    async (id: string, payload: Partial<Insurance>) => {
      setLoading(true);
      setError(null);
      try {
        const data = await insuranceService.updateInsurance(id, payload);
        setInsurances((prev) =>
          prev.map((i) => (i.id === id || i._id === id ? data : i))
        );
        return data;
      } catch (err: any) {
        const errorMsg = err?.message || "Error updating insurance";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteInsurance = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await insuranceService.deleteInsurance(id);
      setInsurances((prev) => prev.filter((i) => i.id !== id && i._id !== id));
      return true;
    } catch (err: any) {
      const errorMsg = err?.message || "Error deleting insurance";
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // New method: Fetch family insurances, optionally excluding a member
  const fetchFamilyInsurances = useCallback(
    async (familyId?: string, excludeMemberId?: string) => {
      if (!memberInfo?.id) return [];

      setLoading(true);
      setError(null);
      try {
        const targetFamilyId =
          familyId || memberInfo.current_family || memberInfo.familyId;
        if (!targetFamilyId) {
          throw new Error("No family ID available");
        }

        const data = await insuranceService.getInsurancesByFamily(
          targetFamilyId,
          excludeMemberId
        );
        const safeData = Array.isArray(data) ? data : [];

        setFamilyInsurances((prev) => {
          const stringifiedPrev = JSON.stringify(prev);
          const stringifiedNew = JSON.stringify(safeData);
          return stringifiedPrev === stringifiedNew ? prev : safeData;
        });

        return safeData;
      } catch (err: any) {
        const errorMsg = err?.message || "Error fetching family insurances";
        console.error(errorMsg, err);
        setError(errorMsg);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [memberInfo?.id, memberInfo?.current_family, memberInfo?.familyId]
  );

  // New method: Link family member to insurance policy
  const linkFamilyMember = useCallback(
    async (insuranceId: string, memberId: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await insuranceService.linkFamilyMember(
          insuranceId,
          memberId
        );
        // Update both insurances and familyInsurances if they contain this insurance
        setInsurances((prev) =>
          prev.map((i) =>
            i.id === insuranceId || i._id === insuranceId ? data : i
          )
        );
        setFamilyInsurances((prev) =>
          prev.map((i) =>
            i.id === insuranceId || i._id === insuranceId ? data : i
          )
        );
        return data;
      } catch (err: any) {
        const errorMsg = err?.message || "Error linking family member";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const unlinkFamilyMember = async (insurance: Insurance, memberId: string) => {
    if (!insurance || !memberId) throw new Error("Invalid arguments");
    const updatedCovered = (insurance.coveredMembers || []).filter(
      (id) => id !== memberId
    );
    return updateInsurance(insurance._id || insurance.id, {
      coveredMembers: updatedCovered,
    });
  };

  return {
    insurances,
    familyInsurances,
    loading,
    error,
    fetched,
    fetchInsurance,
    createInsurance,
    updateInsurance,
    deleteInsurance,
    fetchFamilyInsurances,
    linkFamilyMember,
    unlinkFamilyMember,
  } as const;
}
